import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { AdminRsvp, EventAttendee, ScheduledEvent, SmsSettingsPublic } from "@tih/shared";
import {
  attendanceCsvFilename,
  buildAttendanceCsv,
  hasPermission,
} from "@tih/shared";
import { useAuth } from "../../context/AuthContext";
import {
  ApiError,
  adminCheckOutAttendee,
  adminEmailAttendanceReport,
  adminFetchEventAttendees,
  adminFetchEvents,
  adminFetchFeaturedEvent,
  adminFetchRsvps,
  adminFetchSettingsSms,
} from "../../api/client";
import { AdminSmsCompose } from "../../components/admin/AdminSmsCompose";
import { pickDefaultCheckInEvent } from "../../lib/attendee-directory";
import { formatCheckInTime } from "../../lib/attendeeBulkParse";
import {
  eventDisplayTitle,
  formatEventWhen,
  phaseLabel,
  rowPhase,
} from "../../lib/event-admin-utils";

function parseExtraEmails(raw: string): string[] {
  return [...new Set(raw.split(/[,;\s]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];
}

export function AdminAttendancePage() {
  const { user } = useAuth();
  const canSms = hasPermission(user, "sms");
  const [events, setEvents] = useState<ScheduledEvent[]>([]);
  const [eventId, setEventId] = useState<number | null>(null);
  const [attendees, setAttendees] = useState<EventAttendee[]>([]);
  const [rsvps, setRsvps] = useState<AdminRsvp[]>([]);
  const [sms, setSms] = useState<SmsSettingsPublic | null>(null);
  const [extraEmails, setExtraEmails] = useState("");
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const selectedEvent = events.find((e) => e.id === eventId) ?? null;
  const now = Date.now();

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, featured, smsSettings] = await Promise.all([
        adminFetchEvents(),
        adminFetchFeaturedEvent(),
        canSms ? adminFetchSettingsSms().catch(() => null) : Promise.resolve(null),
      ]);
      setEvents(rows);
      setSms(smsSettings);
      setEventId((prev) => prev ?? pickDefaultCheckInEvent(rows, featured?.id ?? null));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load events");
    } finally {
      setLoading(false);
    }
  }, [canSms]);

  const loadAttendees = useCallback(async (opts?: { silent?: boolean }) => {
    if (eventId == null) return;
    if (!opts?.silent) setListLoading(true);
    try {
      const [rows, rsvpRows] = await Promise.all([
        adminFetchEventAttendees(eventId),
        adminFetchRsvps({ eventId }),
      ]);
      setAttendees(rows);
      setRsvps(rsvpRows);
    } catch (e) {
      if (!opts?.silent) {
        setError(e instanceof ApiError ? e.message : "Could not load attendance");
      }
    } finally {
      if (!opts?.silent) setListLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    void loadAttendees();
  }, [loadAttendees]);

  useEffect(() => {
    if (eventId == null) return;
    const id = window.setInterval(() => {
      void loadAttendees({ silent: true });
    }, 5000);
    return () => window.clearInterval(id);
  }, [eventId, loadAttendees]);

  const checkedInRows = useMemo(
    () =>
      attendees
        .filter((a) => a.checkedInAt)
        .sort((a, b) => Date.parse(b.checkedInAt ?? "") - Date.parse(a.checkedInAt ?? "")),
    [attendees],
  );
  const checkedIn = checkedInRows.length;
  const eventTitle = selectedEvent ? eventDisplayTitle(selectedEvent) : "attendance";

  async function undoCheckIn(attendee: EventAttendee) {
    setBusyId(attendee.id);
    setError(null);
    try {
      await adminCheckOutAttendee(attendee.id);
      setSuccess(`Undid check-in for ${attendee.name}.`);
      await loadAttendees();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  function downloadCsv() {
    const csv = buildAttendanceCsv(checkedInRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = attendanceCsvFilename(eventTitle);
    a.click();
    URL.revokeObjectURL(url);
    setSuccess("Downloaded the attendance CSV.");
  }

  async function emailReport() {
    if (eventId == null) return;
    const extra = parseExtraEmails(extraEmails);
    const invalid = extra.filter((e) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (invalid.length > 0) {
      setError(`Invalid email${invalid.length === 1 ? "" : "s"}: ${invalid.join(", ")}`);
      return;
    }
    setExportBusy(true);
    setError(null);
    try {
      const result = await adminEmailAttendanceReport(eventId, {
        ...(extra.length ? { to: extra } : {}),
      });
      setSuccess(
        `Attendance report emailed to ${result.sentTo.join(", ")} (${result.checkedIn} checked in).`,
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not email attendance report");
    } finally {
      setExportBusy(false);
    }
  }

  if (loading) {
    return <div className="admin-page" style={{ opacity: 0.5 }}>Loading…</div>;
  }

  return (
    <div className="admin-page admin-checkin-page">
      <header className="admin-page-header">
        <h1>Attendance</h1>
        <p>
          Live checked-in report for tonight. Updates as visitors arrive at the{" "}
          <Link to="/admin/check-in">check-in desk</Link> or on the self check-in tablet.
          Download a CSV, email the report, or text guests who have mobile numbers.
        </p>
      </header>

      {error && <div className="form-error admin-banner">{error}</div>}
      {success && (
        <div className="form-success admin-banner" onAnimationEnd={() => setSuccess(null)}>
          {success}
        </div>
      )}

      <div className="admin-card admin-checkin-toolbar">
        <label className="admin-field">
          <span>Event</span>
          <select
            value={eventId ?? ""}
            onChange={(e) => setEventId(Number(e.target.value) || null)}
          >
            {events.length === 0 && <option value="">No events</option>}
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {eventDisplayTitle(ev)} · {formatEventWhen(ev.startsAt)} (
                {phaseLabel(rowPhase(ev, now))})
              </option>
            ))}
          </select>
        </label>
        {selectedEvent && (
          <p className="admin-muted admin-checkin-stats">
            {checkedIn} checked in · {attendees.length} on tonight’s list
          </p>
        )}
        <Link to="/admin/check-in" className="btn btn-ghost">
          Check-in desk
        </Link>
      </div>

      <section className="admin-card" style={{ marginBottom: 20 }}>
        <h2 className="admin-card-title">Share this report</h2>
        <p className="admin-card-sub">
          CSV lists everyone currently checked in. Email uses SMTP from{" "}
          <Link to="/admin/settings/email">Settings → Email</Link> and always includes your
          admin address.
        </p>
        <div className="admin-blog-form">
          <div className="admin-blog-form-fields">
            <label>
              <span>Also email</span>
              <input
                value={extraEmails}
                disabled={exportBusy}
                onChange={(e) => setExtraEmails(e.target.value)}
                placeholder="optional extra addresses, comma-separated"
              />
            </label>
          </div>
          <div className="admin-blog-form-actions">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={checkedIn === 0}
              onClick={downloadCsv}
            >
              Download CSV
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={exportBusy || eventId == null}
              onClick={() => void emailReport()}
            >
              {exportBusy ? "Sending…" : "Email report"}
            </button>
          </div>
        </div>
      </section>

      {canSms && (
        <AdminSmsCompose
          eventId={eventId}
          attendees={attendees}
          rsvps={rsvps}
          sms={sms}
          onStatus={(kind, message) => {
            if (kind === "error") setError(message);
            else setSuccess(message);
          }}
        />
      )}

      <section className="admin-card admin-checkin-arrived" style={{ marginTop: 20 }}>
        <h2 className="admin-card-title">Checked in tonight</h2>
        <p className="admin-card-sub">
          Newest arrivals first. Refreshing automatically.
        </p>
        {listLoading ? (
          <p>Loading attendance…</p>
        ) : checkedInRows.length === 0 ? (
          <p className="admin-muted">No one has checked in yet.</p>
        ) : (
          <ul className="admin-checkin-list">
            {checkedInRows.map((a) => (
              <li key={a.id} className="admin-checkin-row is-checked-in">
                <div className="admin-checkin-row-main">
                  <strong>{a.name}</strong>
                  <span className="admin-checkin-row-meta">
                    {[a.mobile, a.email, a.location].filter(Boolean).join(" · ") || "—"}
                  </span>
                  {a.checkedInAt && (
                    <span className="admin-checkin-time">
                      In at {formatCheckInTime(a.checkedInAt)}
                      {a.checkedInByName ? ` · ${a.checkedInByName}` : ""}
                    </span>
                  )}
                </div>
                <div className="admin-checkin-row-actions">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={busyId === a.id}
                    onClick={() => void undoCheckIn(a)}
                  >
                    Undo
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
